/*
*   @site           Preloaded: Site
*   @function       Module: Header
*   @author         Last17
*   @copyright      Copyright 2025, Preloaded.com
*   @version        0.01
*
*********************************************************************************************/


//-----------------------------------------------
// Imports
//-----------------------------------------------

//Template
import ModuleTemplate from '../_templates/ModuleTemplate';

//Library
import { gsap } from 'gsap';

//-----------------------------------------------
// Class Definition
//-----------------------------------------------
export default class ModuleCarouselLogos extends ModuleTemplate {

  //-----------------------------------------------
  // Init
  //-----------------------------------------------
  init() {
    //Get the initial set of items
    let list = this.element.querySelectorAll('.logo');


    //Config
    this.config = {
      'method': this.element.getAttribute('data-scroll-method'),
      'speed': parseFloat(this.element.getAttribute('data-scroll-speed')),
      'position' : {
        current: 0,
        target: 0
      },
      'lag': 5
    };

    //Track
    this.track = this.element.querySelector('.track');

    //Calculate the items
    this.items = [];
    let max = list.length;
    let clone;
    for(let i = 0; i < max; i++) {
      //Add item to the list
      this.items.push({
        element: list[i],
        isClone: false
      });
    }

    //Scroll events
    if(this.items.length > 0) {
      if(this.config.method === 'scroll') {
        //Listen for scroll
        this.subscribeToEvent('scroll');
      }
    }
  }


  //-----------------------------------------------
  // Set layout (startup and when we switch between mobile and desktop)
  //-----------------------------------------------
  setLayout(layout) {
    //Parent
    super.setLayout(layout);

    //Scroll params
    if(this.items.length > 0) {
      this.calculateScrollParams();
    }
  }


  //-----------------------------------------------
  // Set position
  //-----------------------------------------------
  calculateScrollParams() {
    //Kill any animation up front
    gsap.killTweensOf(this.track);

    //Calculate the full width of the carousel and the width of a group of non cloned items
    this.config.contentWidth = 0;
    this.config.firstGroupWidth = 0;
    let width;
    for(let i = 0; i < this.items.length; i++) {
      //Calculate full item width including margins
      width = this.getFullItemWidth(this.items[i].element);

      //Add to overall content width
      this.config.contentWidth += width;
      
      //Add to first group width if not a cloned item
      if(!this.items[i].isClone) {
        this.config.firstGroupWidth += width;
      }
    }

    //Init
    if(this.config.method === 'scroll') {
      //Ca
      this.config.scrollMax =  window.innerWidth - this.config.contentWidth;
      this.setPosition();
    } else {
      //Adjust the items until we have got the correct amount
      while(this.config.contentWidth < Math.max((window.innerWidth * 2), (this.config.firstGroupWidth * 2))) {
        //Clone the first group of items until we have enough to fill the screen twice over
        let clone;
        for(let i = 0; i < this.items.length; i++) {
          if(!this.items[i].isClone) {
            //Clone the items
            clone  = this.items[i].element.cloneNode(true);
            this.track.appendChild(clone);
            this.items.push({
              element: clone,
              isClone: true
            });
            
            //Add to the width
            this.config.contentWidth += this.getFullItemWidth(this.items[i].element);
          } else {
            //Stop cloning
            break;
          }
        }
      }

      //Reset timed animation
      if(this.config.contentWidth > window.innerWidth) {
        //Maximum scroll is one group
        this.config.scrollMax = -this.config.firstGroupWidth;
        gsap.fromTo(this.track, {x: '0vw'}, { x: this.config.scrollMax + 'px', duration: this.config.speed, ease: 'none', repeat: -1});
      } else {
        //Place it in the middle
        this.track.style['transform'] = 'translate(' + ((window.innerWidth - this.config.contentWidth) / 2) + 'px, 0px)';
      }
    }
  }


  //-----------------------------------------------
  // Item width
  //-----------------------------------------------
  getFullItemWidth(item) {
    let style = document.defaultView.getComputedStyle(item);
    return item.offsetWidth + parseFloat(style.marginLeft) + parseFloat(style.marginRight);
            
  }

  //-----------------------------------------------
  // Set position
  //-----------------------------------------------
  setPosition() {
    if(this.config.contentWidth > window.innerWidth) {
      //Scroll Bounds
      let scrollBounds = this.getScrollBounds(1);
      let prop = (window.scrollY - scrollBounds.min) / (scrollBounds.max - scrollBounds.min);
      prop = Math.min(1, Math.max(0, prop)); //clamped
    
      //Set the target
      this.config.position.target = (prop * this.config.scrollMax);

      //Mover
      if (this.stepper) {
        cancelAnimationFrame(this.stepper);
        this.stepper = null;
      }
      this.stepper = requestAnimationFrame(() => { this.animationUpdate(); });
    } else {
      //Place it in the middle
      this.track.style['transform'] = 'translate(' + ((window.innerWidth - this.config.contentWidth) / 2) + 'px, 0px)';
    }
  }

  
  //-----------------------------------------------
  // Scroll event
  //-----------------------------------------------
  scrollEvent(data) {
    //Parent
    super.scrollEvent(data);

    //This
    this.setPosition();
  }


  //-----------------------------------------------
  // Custom resize
  //-----------------------------------------------
  onResize(data) {
    //Calculate size
    if(data.changed.width) {
      this.calculateScrollParams();
    }
  }


  //-----------------------------------------------
  // Animation update
  //-----------------------------------------------
  animationUpdate() {
    //Movement
    if(this.config.position.current !== this.config.position.target) {
      let inc = (this.config.position.target - this.config.position.current) / this.config.lag;
      let positionNew = (inc < 0.001) ? this.config.position.target : this.config.position.current + inc;
      this.track.style['transform'] = 'translate(' + (positionNew) + 'px, 0px)';
      this.config.position.current = positionNew;
    }

    //Go again if not in position
    if(this.config.position.current !== this.config.position.target) {
      this.stepper = requestAnimationFrame(() => { this.animationUpdate(); });
    }  else {
      this.stepper = null;
    }
  }

}